import React, { useCallback, useEffect, useState } from 'react';
import { AllieText, AllieCheckbox, AllieButton, AllieModal } from '../../../components';
import { AllieCheckmarkOutlineIcon, AllieCloseOutlineIcon } from '../../../styling/icons';
import {
  ButtonContainer,
  FeedbackIcon,
  ModalContent,
  VariableItem,
  VariableList,
} from './SnakeContainer.styles';

interface Variable {
  id: number;
  name: string;
  isCustomDisplay: boolean;
  isMacroView: boolean;
}

interface VariableConfigModalProps {
  configVarLabel: Record<string, string>;
  isOpen: boolean;
  onClose: () => void;
  machineName: string;
  variables: Variable[] | undefined;
  onSave: (selectedVariables: Variable[]) => Promise<void>;
  loadingUpdateVariableDisplay: boolean;
  mutationSuccess: boolean | null;
  isMobile: boolean;
}

const VariableConfigModal: React.FC<VariableConfigModalProps> = ({
  configVarLabel,
  isOpen,
  onClose,
  machineName,
  variables = [],
  onSave,
  loadingUpdateVariableDisplay,
  mutationSuccess,
  isMobile,
}) => {
  const [selectedVariables, setSelectedVariables] = useState<Variable[]>([]);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedVariables(variables);
    }
  }, [isOpen, variables]);

  useEffect(() => {
    const selectedCount = selectedVariables.filter((v) => v.isCustomDisplay).length;
    setIsButtonDisabled(selectedCount > 3 || loadingUpdateVariableDisplay);
  }, [selectedVariables, loadingUpdateVariableDisplay]);

  useEffect(() => {
    if (mutationSuccess !== null) {
      setShowFeedback(true);
    }
  }, [mutationSuccess]);

  const handleVariableToggle = (id: number, checked: boolean) => {
    setSelectedVariables((prev) =>
      prev.map((v) => (v.id === id ? { ...v, isCustomDisplay: checked } : v))
    );
  };

  const handleSave = useCallback(async () => {
    setIsButtonDisabled(true);
    await onSave(selectedVariables);
    setIsButtonDisabled(false);
  }, [onSave, selectedVariables]);

  const handleClose = useCallback(() => {
    setShowFeedback(false);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <AllieModal
      isOpen={isOpen}
      backdropDismiss={true}
      width={'360px'}
      height={isMobile ? 'unset' : 'auto'}
      onDidDismiss={() => {
        handleClose();
        return Promise.resolve({});
      }}
      style={{ '--border-radius': 'var(--allie-radius-xl)' }}
    >
      <ModalContent
        onClick={(e: CustomEvent) => e.stopPropagation()}
        mode="md"
        header={{
          children: (
            <AllieText color="primary-90" weight={600} type="body1">
              {configVarLabel.header}
            </AllieText>
          ),
        }}
        content={{
          children: (
            <>
              {showFeedback ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 'var(--allie-spacing-3xl)',
                  }}
                >
                  <FeedbackIcon success={mutationSuccess}>
                    {mutationSuccess ? (
                      <AllieCheckmarkOutlineIcon color="success-main" />
                    ) : (
                      <AllieCloseOutlineIcon color="error-main" />
                    )}
                  </FeedbackIcon>
                  <AllieText color="neutral-70" type="body1">
                    {mutationSuccess
                      ? configVarLabel.variableUpdateSuccess
                      : configVarLabel.variableUpdateError}
                  </AllieText>
                </div>
              ) : (
                <>
                  <VariableList>
                    {selectedVariables.map((variable) => (
                      <VariableItem key={`${machineName}-${variable.id}`}>
                        <AllieCheckbox
                          size={16}
                          checked={variable.isCustomDisplay}
                          onIonChange={(e: CustomEvent) =>
                            handleVariableToggle(variable.id, e.detail.checked)
                          }
                        />
                        <AllieText color="neutral-70" weight={500}>
                          {variable.name}
                        </AllieText>
                      </VariableItem>
                    ))}
                  </VariableList>
                  <ButtonContainer>
                    <AllieButton
                      full
                      allieSize="small"
                      corner="rounded-edges-plus"
                      onclick={handleSave}
                      disabled={isButtonDisabled}
                    >
                      {loadingUpdateVariableDisplay
                        ? configVarLabel.saveButtonLoading
                        : configVarLabel.saveButton}
                    </AllieButton>
                    {mutationSuccess && (
                      <AllieText type="body2" color="neutral-70">
                        {configVarLabel.variableUpdateSuccess}
                      </AllieText>
                    )}
                  </ButtonContainer>
                </>
              )}
            </>
          ),
        }}
      />
    </AllieModal>
  );
};

export default VariableConfigModal;
